/**
 * The body of `/tax-exemption`, where a signed-in customer uploads a tax exemption certificate:
 * a title, a one-line instruction, and the contact form with `topic` fixed to
 * `taxExemptDocuments` and a file required.
 *
 * Rendered only through `components/NextLayouts/ProtectedRoute.tsx` → `ProtectedRoute` with
 * `Permission.Tax_Exempt_Submit` (`pages/tax-exemption/index.tsx`), which only decides who sees
 * the form: the Common API route it posts to is public and checks no permission, by design
 * (see that file's header). With that topic,
 * `components/ContactFormWithFileAttacher/index.tsx` hides the topic dropdown and sends no GA
 * event. The Common API sends the message to its tax exemption mailbox (in production; other
 * environments use one shared test mailbox) and writes a Magenium
 * communication log entry for the signed-in user
 * (`web-common-api/application/src/repos/communication.repo.ts` → `contactUs`).
 */
import React from "react";

import { ContactFormWithFileAttacher } from "@components/ContactFormWithFileAttacher";
import { Typography } from "@ryerson/frontend.typography";
import { ContentSection } from "@ryerson/frontend.layout/dist/ContentSection";
import { useTheme } from "@ryerson/frontend.theme";
import { useApplication } from "@ryerson/frontend.application/dist/Application";
import { withLanguage } from "./TaxExemption.Content";
import { mainContainer, titleContainerStyle } from "./TaxExemptionPage.Styled";
import { Flex } from "@ryerson/frontend.layout/dist/Flex";
import { AllFormTopics } from "@components/ContactFormWithFileAttacher/ContactFormWithFile.Models";

import { useWindowSize } from "@ryerson/frontend.common";

/** The tax exemption page body; see the file header. */
export const TaxExemptionPage: React.FC = () => {
	const { theme } = useTheme();

	const {
		localization: { language },
	} = useApplication();

	const content = withLanguage[language];
	let windowSize = useWindowSize()[0];
	let isMobile = windowSize < parseInt(theme.breakpoints.lg);

	return (
		<ContentSection
			type="tertiary"
			vPadding={isMobile ? "22px" : "50px"}
			hPadding={isMobile ? "20px" : "0px"}
		>
			<Flex direction="column" alignContent="flex-start" css={titleContainerStyle(theme)}>
				<Typography css={{ marginBottom: 24 }} variant="h1" type="tertiary" size="md">
					{content.title}
				</Typography>
				<Typography size="md" type="tertiary">
					{content.subTitle}
				</Typography>
			</Flex>
			<Flex direction="column" css={mainContainer(theme)}>
				<ContactFormWithFileAttacher
					fileRequired={true}
					topic={AllFormTopics.taxExemptDocuments}
				/>
			</Flex>
		</ContentSection>
	);
};
